<template>
    <div class="form-input__wrap form-input__wrap--multiselect" ref="wrap">
        <InputLabel :for="_uid">
                {{prettyName}}
                <span class="form-input__requirement" v-if="$slots.requirement">
                    <slot name="requirement"></slot>
                </span>
        </InputLabel>
        <ValidationProvider
                :name="prettyName"
                :rules="validationRules"
                v-slot="{ errors }"
                persist>
            <Multiselect
                    :id="_uid"
                    :multiple="multiple"
                    :track-by="trackBy"
                    :close-on-select="!multiple"
                    :label="label"
                    v-model="internalValue"
                    :placeholder="placeholder"
                    @open="safariTabFix"
                    @close="safariTabFix"
                    :options="options" />
            <span class="form-input__errors">{{ errors[0] }}</span>
            <span class="form-input__errors form-input__errors--server" v-for="(e, index) in responseErrors" :key="index">{{e}}<br></span>
        </ValidationProvider>
    </div>
</template>

<script>
    import InputLabel from './InputLabel'

    // For Safari fix

    const isDisplayed = (element) => {
        return (element.offsetParent !== null)
    }

    // Find next element sibling that is not hidden, or finds one from the parentNode's next sibling.
    function nextElSibling (element) {
        if (!element) return;

        const closest = element.nextElementSibling;

        if (closest && !isDisplayed(closest)) {
            return nextElSibling(closest);
        }

        if (!closest) {
            const closestAdjacentParentChild = nextElSibling(element.parentNode).firstElementChild;
            if (isDisplayed(nextElSibling(element.parentNode)) && isDisplayed(closestAdjacentParentChild)) {
                return closestAdjacentParentChild;
            }

            if (!nextElSibling(closestAdjacentParentChild)) {
                return nextElSibling(element.parentNode);
            }

            return nextElSibling(closestAdjacentParentChild);
        }

        return closest;
    }

    // Same as nextElSibling but backwards
    function prevElSibling (element) {
        if (!element) return;

        const closest = element.previousElementSibling;

        if (closest && !isDisplayed(closest)) {
            return prevElSibling(closest);
        }

        if (!closest) {
            const closestAdjacentParentChild = prevElSibling(element.parentNode).lastElementChild;
            if (isDisplayed(prevElSibling(element.parentNode)) && isDisplayed(closestAdjacentParentChild)) {
                return closestAdjacentParentChild;
            }

            if (!prevElSibling(closestAdjacentParentChild)) {
                return prevElSibling(element.parentNode);
            }

            return prevElSibling(closestAdjacentParentChild);
        }

        return closest;
    }


    export default {
        name: 'FormMultiselectInput',

        data () {
            return {
                errors: [],
                internalValue: this.initialValue || null
            }
        },

        components: {
            InputLabel,
        },

        props: {
            initialValue: [String, Object, Array],
            value: [String, Object, Array],
            validationRules: String,
            prettyName: String,
            name: String,
            placeholder: String,
            options: Array,
            responseErrors: Array,
            label: {
                type: String,
                required: false
            },
            trackBy: {
                type: String,
                required: false
            },
            multiple: {
                type: Boolean,
                default: false
            },
            tabFix: {
                type: Boolean,
                default: false
            }
        },

        methods: {
            safariTabFix () {
                // Hacky Safari tab navigation fix
                if (!this.tabFix) return;

                const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                if (!isSafari()) return;

                const findSubmitButton = element => {
                    const parent = element.parentNode;
                    const button = parent.querySelector('[type="submit"]');
                    if (!button) return findSubmitButton(parent);
                    return button;
                }

                const findAndFocus = previous => {
                    // Find next/previous sibling and click on multiselect or focus regular HTML Input
                    const currentElement = this.$refs.wrap;
                    const nextSibling = previous ? prevElSibling(currentElement) : nextElSibling(currentElement);
                    if (!nextSibling ||
                        !nextSibling.querySelector('input:not([type="hidden"]), textarea:not(.g-recaptcha-response), select, button, .multiselect')) {
                        return findSubmitButton(currentElement).focus();
                    }

                    const siblingTarget = node => node && node.querySelector('input:not([type="hidden"]), textarea:not(.g-recaptcha-response), select, button');
                    const multiselectTarget = node => node && node.querySelector('.multiselect');

                    multiselectTarget(nextSibling) && multiselectTarget(nextSibling).click();
                    siblingTarget(nextSibling) && siblingTarget(nextSibling).focus();
                };

                const listenKeyPresses = () => {
                    if (document.activeElement !== document.body) return;

                    document.addEventListener("keypress", keyDownHandler);
                    document.addEventListener("keydown", keyDownHandler);
                    document.addEventListener("click", cancelListeners)
                };

                const cancelListeners = () => {
                    document.removeEventListener("keypress", keyDownHandler);
                    document.removeEventListener("keydown", keyDownHandler);
                    document.removeEventListener("click", cancelListeners);
                };

                const keyDownHandler = event => {
                    // Unless shift, remove event listeners.
                    if (event.key === 'Shift' || event.keyCode === 16 || event.which === 16) return;
                    cancelListeners();

                    // Unless tab, do nothing.
                    if (event.which !== 9 && event.key !== "Tab" && event.key !== 'tab') return;
                    event.preventDefault();

                    findAndFocus(event.shiftKey);
                }

                setTimeout(listenKeyPresses, 0);
            }
        },
        watch: {
            value (newValue) {
                this.internalValue = newValue;
            },
            internalValue (newValue) {
                this.$emit('input', newValue);
            }
        }
    }
</script>

<style lang="scss" scoped>
    .hidden-select {
        display: none;
    }

</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
    @import "styleguidescss/utilities/_variables.scss";

    $tutorial-editor-border-radius: 2px;

    .multiselect {
        &__tags {
            border-color: $ash-gray;
            border-radius: $tutorial-editor-border-radius;

            .error & { border-color: $red; }
        }

        &__content {
            border-bottom-left-radius: $tutorial-editor-border-radius;
            border-bottom-right-radius: $tutorial-editor-border-radius;
        }

        &--above .multiselect__content {
            border-top-left-radius: $tutorial-editor-border-radius;
            border-top-right-radius: $tutorial-editor-border-radius;
        }

        &__option {
            font-weight: normal;
            font-size: 14px;

            &--highlight {
                color: $dark-gray;
                background-color: $light-gray;

                &:after {
                    display: none;
                }
            }

            &--selected, &--selected.multiselect__option--highlight {
                color: $blue;
                font-weight: inherit;
                background-color: $light-gray;
            }
        }

        &__select:before {
            border-color: $dark-gray transparent transparent;
        }


        &__single {
            padding-left:0;
            user-select: none;
            cursor: pointer;
            font-weight: normal;
            color: $dark-gray;
            font-size: 14px;
        }


        &__input {
            padding-left: 0;
            margin-bottom: 0;
        }

        &__input, &__placeholder {
            font-size: 14px;
            font-weight: normal;
        }

        &__placeholder {
            color: #999;
            opacity: 0.5;
            padding-top: 0;
        }

        &__spinner:after, &__spinner:before {
            border-color: $blue transparent transparent;
        }

        &__tag {
            background: $blue;
            border-bottom: 1px solid darken($blue, 10%);
            font-weight: bold;
            font-size: 12px;
            &-icon {
                transition: background 0.1s;
                border-bottom-left-radius: 0;

                &:after {
                    color: rgba(0,0,0,0.4);
                    transition: color 0.1s;
                }

                &:hover {
                    background: darken($blue, 10%);
                }
            }
        }
    }

    .multiselect__input::placeholder {
        color: #999;
        opacity: 0.5;
    }
</style>